import {
  OrderBookConfigurationKeys,
  OrderBookConfigurations,
  ORDER_BOOK_NAMESPACE,
} from '@epicuro-next/data/order-book';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { createSelector } from '@ngrx/store';

import { DetailPageConfigModel, IOrderMaterialTableSchema } from '../models';

import {
  defaultSortMapping,
  defaultSortRule,
} from './list/soring-map/default-sorting-map.config';

const defaultConfig = JSON.stringify({});
const defaultDetailsConfig = JSON.stringify({});

export const selectOrderMaterialTableSchema = createSelector(
  selectConfigurationValueFrom<
    OrderBookConfigurationKeys.orderListTableSchema,
    OrderBookConfigurations
  >(
    ORDER_BOOK_NAMESPACE,
    OrderBookConfigurationKeys.orderListTableSchema,
    defaultConfig,
  ),
  (config) => {
    try {
      return JSON.parse(config as string) as IOrderMaterialTableSchema;
    } catch {
      console.error('Failed to parse order book config', config);
      return null;
    }
  },
);

export const selectOrderMaterialRowColumns = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.orderMaterialRowColumns,
);

export const selectOrderMaterialGroupColumns = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.orderMaterialGroupColumns,
);

export const selectOrderMaterialHeaderColumns = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.orderMaterialHeaderColumns,
);

export const selectOrderMaterialFooterColumns = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.orderMaterialFooterColumns,
);

export const selectMaterialGridTemplateStyles = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.materialGridTemplateStyles,
);

export const selectListGridCellTemplateItems = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.gridCellTemplate,
);

export const selectOrderFiltersConfig = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.filters,
);

export const selectInitialFilterCount = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.initialFilterCount,
);

export const selectSelectOrdersEnabled = createSelector(
  selectOrderMaterialTableSchema,
  (schemas) => schemas?.selectOrdersEnabled,
);

export const selectIsMultiAccountEnabled = createSelector(
  selectOrderMaterialTableSchema,
  (schemas) => !!schemas?.enableMultiAccount,
);

export const selectShowStyleInfoInSizeGrid = createSelector(
  selectOrderMaterialTableSchema,
  (schemas) => !!schemas?.showStyleInfoInSizeGrid,
);

export const selectSortMapping = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.sortMapping ?? defaultSortMapping,
);

export const selectDefaultSortRule = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.defaultSortRule ?? defaultSortRule,
);

export const selectIsDownloadPdfEnabled = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.isDownloadPdfEnabled,
);

export const selectIsGroupingEnabled = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.isGroupingEnabled,
);

export const selectIsListExportSelectedEnabled = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => !!schema?.isListExportSelectedEnabled,
);

export const selectListFiltersExportMap = createSelector(
  selectOrderMaterialTableSchema,
  (schema) => schema?.filtersExportMap ?? {},
);

export const selectOrderDetailsSchema = createSelector(
  selectConfigurationValueFrom<
    OrderBookConfigurationKeys.orderDetailsSchema,
    OrderBookConfigurations
  >(
    ORDER_BOOK_NAMESPACE,
    OrderBookConfigurationKeys.orderDetailsSchema,
    defaultDetailsConfig,
  ),
  (config) => {
    try {
      // For create rule for config
      // console.log rule and run JSON.stringify(config) + replaceAll(/\\n/g, '')
      // check on json validator (you can have issue when transform function has join(\' - \') - then delete \
      return JSON.parse(config as string) as DetailPageConfigModel;
    } catch {
      console.error('Failed to parse order book config', config);
      return null;
    }
  },
);

export const selectDetailPageDefaultSortRule = createSelector(
  selectOrderDetailsSchema,
  (schema) => schema?.defaultSortRule,
);

export const selectDetailGridCellTemplateItems = createSelector(
  selectOrderDetailsSchema,
  (schema) => schema?.gridCellTemplateItems,
);

export const selectDetailTableRowItems = createSelector(
  selectOrderDetailsSchema,
  (schema) => schema?.tableRow,
);

export const selectDetailOrderItemsGroupingProps = createSelector(
  selectOrderDetailsSchema,
  (schema) => schema?.orderItemsGroupingProperties ?? [],
);

export const selectDetailTableRowMoreItems = createSelector(
  selectOrderDetailsSchema,
  (schema) => schema?.tableRowMoreItems,
);

export const selectDetailGridCellCustomRowsConfig = createSelector(
  selectOrderDetailsSchema,
  (schema) => schema?.gridCellCustomRowsConfig,
);

export const selectDetailGridCellRowTitleConfig = createSelector(
  selectOrderDetailsSchema,
  (schema) => schema?.gridCellRowTitleProperty,
);

export const selectDetailPageStyles = createSelector(
  selectOrderDetailsSchema,
  (schema) => schema?.styles,
);

export const selectShowSizeGridTitle = createSelector(
  selectOrderDetailsSchema,
  (schema) => Boolean(schema?.showSizeGridTitle),
);
